import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ILicenseTypesFilterOptions } from '@app/api/licencetypeEntity.api';
import { tableDataInit } from '@app/pages/MasterData/LicenseTypes/consts';
import { IPagination } from '@app/interfaces/pagination';
import { initialPagination } from '@app/constants/pagination';

export interface LicenseTypesFilterState {
  filterOptions: ILicenseTypesFilterOptions;
  shouldTriggerFetch: boolean;

  pagination: IPagination;

  providerSearch: string;
  typecodeSearch: string;
  titleSearch: string;

  selectedRangeTypes: string[];
  selectedRangeDays: number[];
  selectedSalesPoints: number[];

  localsOnly: boolean | null;
  sanaRequired: boolean | null;
  licenseReturn: boolean | null;

  providerSearchTemporal: string;
  typecodeSearchTemporal: string;
  titleSearchTemporal: string;
  selectedRangeTypesTemporal: string[];
  selectedRangeDaysTemporal: number[];
  selectedSalesPointsTemporal: number[];
  localsOnlyTemporal: boolean | null;
  sanaRequiredTemporal: boolean | null;
  licenseReturnTemporal: boolean | null;
}

const initialState: LicenseTypesFilterState = {
  filterOptions: tableDataInit.filterOptions,
  shouldTriggerFetch: false,
  pagination: initialPagination,

  // Applied filters
  providerSearch: '',
  typecodeSearch: '',
  titleSearch: '',
  selectedRangeTypes: [],
  selectedRangeDays: [],
  selectedSalesPoints: [],
  localsOnly: null,
  sanaRequired: null,
  licenseReturn: null,

  // Temporal filters
  providerSearchTemporal: '',
  typecodeSearchTemporal: '',
  titleSearchTemporal: '',
  selectedRangeTypesTemporal: [],
  selectedRangeDaysTemporal: [],
  selectedSalesPointsTemporal: [],
  localsOnlyTemporal: null,
  sanaRequiredTemporal: null,
  licenseReturnTemporal: null,
};

const licenseTypesFiltersSlice = createSlice({
  name: 'licenseTypesFilters',
  initialState,
  reducers: {
    setFilterOptions(state, action: PayloadAction<ILicenseTypesFilterOptions>) {
      state.filterOptions = action.payload;
    },

    setShouldTriggerFetch(state, action: PayloadAction<boolean>) {
      state.shouldTriggerFetch = action.payload;
    },

    setPagination(state, action: PayloadAction<IPagination>) {
      state.pagination = action.payload;
    },

    setProviderSearch(state, action: PayloadAction<string>) {
      state.providerSearch = action.payload;
    },
    setTypecodeSearch(state, action: PayloadAction<string>) {
      state.typecodeSearch = action.payload;
    },
    setTitleSearch(state, action: PayloadAction<string>) {
      state.titleSearch = action.payload;
    },

    // Applied multi-select filters
    setSelectedRangeTypes(state, action: PayloadAction<string[]>) {
      state.selectedRangeTypes = action.payload;
    },
    setSelectedRangeDays(state, action: PayloadAction<number[]>) {
      state.selectedRangeDays = action.payload;
    },
    setSelectedSalesPoints(state, action: PayloadAction<number[]>) {
      state.selectedSalesPoints = action.payload;
    },

    setLocalsOnly(state, action: PayloadAction<boolean | null>) {
      state.localsOnly = action.payload;
    },
    setSanaRequired(state, action: PayloadAction<boolean | null>) {
      state.sanaRequired = action.payload;
    },
    setLicenseReturn(state, action: PayloadAction<boolean | null>) {
      state.licenseReturn = action.payload;
    },

    setProviderSearchTemporal(state, action: PayloadAction<string>) {
      state.providerSearchTemporal = action.payload;
    },
    setTypecodeSearchTemporal(state, action: PayloadAction<string>) {
      state.typecodeSearchTemporal = action.payload;
    },
    setTitleSearchTemporal(state, action: PayloadAction<string>) {
      state.titleSearchTemporal = action.payload;
    },

    setSelectedRangeTypesTemporal(state, action: PayloadAction<string[]>) {
      state.selectedRangeTypesTemporal = action.payload;
    },
    setSelectedRangeDaysTemporal(state, action: PayloadAction<number[]>) {
      state.selectedRangeDaysTemporal = action.payload;
    },
    setSelectedSalesPointsTemporal(state, action: PayloadAction<number[]>) {
      state.selectedSalesPointsTemporal = action.payload;
    },

    setLocalsOnlyTemporal(state, action: PayloadAction<boolean | null>) {
      state.localsOnlyTemporal = action.payload;
    },
    setSanaRequiredTemporal(state, action: PayloadAction<boolean | null>) {
      state.sanaRequiredTemporal = action.payload;
    },
    setLicenseReturnTemporal(state, action: PayloadAction<boolean | null>) {
      state.licenseReturnTemporal = action.payload;
    },

    applyFilters(state) {
      state.providerSearch = state.providerSearchTemporal;
      state.typecodeSearch = state.typecodeSearchTemporal;
      state.titleSearch = state.titleSearchTemporal;
      state.selectedRangeTypes = state.selectedRangeTypesTemporal;
      state.selectedRangeDays = state.selectedRangeDaysTemporal;
      state.selectedSalesPoints = state.selectedSalesPointsTemporal;
      state.localsOnly = state.localsOnlyTemporal;
      state.sanaRequired = state.sanaRequiredTemporal;
      state.licenseReturn = state.licenseReturnTemporal;

      // Reset pagination to page 1 when applying filters
      state.pagination = { ...state.pagination, current: 1 };
      state.shouldTriggerFetch = true;
    },

    resetFilters(state) {
      state.providerSearch = initialState.providerSearch;
      state.typecodeSearch = initialState.typecodeSearch;
      state.titleSearch = initialState.titleSearch;
      state.selectedRangeTypes = initialState.selectedRangeTypes;
      state.selectedRangeDays = initialState.selectedRangeDays;
      state.selectedSalesPoints = initialState.selectedSalesPoints;
      state.localsOnly = initialState.localsOnly;
      state.sanaRequired = initialState.sanaRequired;
      state.licenseReturn = initialState.licenseReturn;

      state.providerSearchTemporal = initialState.providerSearchTemporal;
      state.typecodeSearchTemporal = initialState.typecodeSearchTemporal;
      state.titleSearchTemporal = initialState.titleSearchTemporal;
      state.selectedRangeTypesTemporal = initialState.selectedRangeTypesTemporal;
      state.selectedRangeDaysTemporal = initialState.selectedRangeDaysTemporal;
      state.selectedSalesPointsTemporal = initialState.selectedSalesPointsTemporal;
      state.localsOnlyTemporal = initialState.localsOnlyTemporal;
      state.sanaRequiredTemporal = initialState.sanaRequiredTemporal;
      state.licenseReturnTemporal = initialState.licenseReturnTemporal;

      state.pagination = { ...initialPagination };
      state.shouldTriggerFetch = true;
    },
  },
});

export const {
  setFilterOptions,
  setShouldTriggerFetch,
  setPagination,
  setProviderSearch,
  setTypecodeSearch,
  setTitleSearch,
  setSelectedRangeTypes,
  setSelectedRangeDays,
  setSelectedSalesPoints,
  setLocalsOnly,
  setSanaRequired,
  setLicenseReturn,
  setProviderSearchTemporal,
  setTypecodeSearchTemporal,
  setTitleSearchTemporal,
  setSelectedRangeTypesTemporal,
  setSelectedRangeDaysTemporal,
  setSelectedSalesPointsTemporal,
  setLocalsOnlyTemporal,
  setSanaRequiredTemporal,
  setLicenseReturnTemporal,
  applyFilters,
  resetFilters,
} = licenseTypesFiltersSlice.actions;

export const areFiltersAppliedSelector = (state: { licenseTypesFilters: LicenseTypesFilterState }) => {
  const {
    providerSearch,
    typecodeSearch,
    titleSearch,
    selectedRangeTypes,
    selectedRangeDays,
    selectedSalesPoints,
    localsOnly,
    sanaRequired,
    licenseReturn,
  } = state.licenseTypesFilters;

  return (
    providerSearch.length > 0 ||
    typecodeSearch.length > 0 ||
    titleSearch.length > 0 ||
    selectedRangeTypes.length > 0 ||
    selectedRangeDays.length > 0 ||
    selectedSalesPoints.length > 0 ||
    localsOnly !== null ||
    sanaRequired !== null ||
    licenseReturn !== null
  );
};

export const activeFiltersCountSelector = (state: { licenseTypesFilters: LicenseTypesFilterState }) => {
  const {
    providerSearch,
    typecodeSearch,
    titleSearch,
    selectedRangeTypes,
    selectedRangeDays,
    selectedSalesPoints,
    localsOnly,
    sanaRequired,
    licenseReturn,
  } = state.licenseTypesFilters;

  let count = 0;
  if (providerSearch.length > 0) count++;
  if (typecodeSearch.length > 0) count++;
  if (titleSearch.length > 0) count++;
  if (selectedRangeTypes.length > 0) count++;
  if (selectedRangeDays.length > 0) count++;
  if (selectedSalesPoints.length > 0) count++;
  if (localsOnly !== null) count++;
  if (sanaRequired !== null) count++;
  if (licenseReturn !== null) count++;

  return count;
};

export const fetchParamsSelector = (state: { licenseTypesFilters: LicenseTypesFilterState }) => {
  const {
    providerSearch,
    typecodeSearch,
    titleSearch,
    selectedRangeTypes,
    selectedRangeDays,
    selectedSalesPoints,
    localsOnly,
    sanaRequired,
    licenseReturn,
  } = state.licenseTypesFilters;

  return {
    provider: providerSearch || undefined,
    typecode: typecodeSearch || undefined,
    title: titleSearch || undefined,
    rangeTypes: selectedRangeTypes.length ? selectedRangeTypes : undefined,
    rangeDays: selectedRangeDays.length ? selectedRangeDays : undefined,
    salesPoints: selectedSalesPoints.length ? selectedSalesPoints : undefined,
    localsOnly,
    sanaNumberRequired: sanaRequired,
    licenseReturn,
  };
};

export const licenseTypesFiltersActions = licenseTypesFiltersSlice.actions;

export const licenseTypesFilters = licenseTypesFiltersSlice.reducer;
